import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Phone, MessageCircle, Check, MapPin, Clock, Shield, Scale, Users, Briefcase } from 'lucide-react';
import { Button } from '../components/ui/button';
import { COMPANY_INFO } from '../mock';
import { InternalLinksSection } from '../components/InternalLinksSection';

const UnfallgutachtenRellingenPage = () => {
  const [formData, setFormData] = useState({ name: '', phone: '', message: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    await fetch(`${API_URL}/api/leads/submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...formData, source: 'unfallgutachten-rellingen-landing-page' })
    });
    alert('Vielen Dank! Wir melden uns schnellstmöglich.');
    setFormData({ name: '', phone: '', message: '' });
  };

  const faqs = [
    {
      q: "Wo genau in Rellingen ist euer Büro?",
      a: "Unser Hauptsitz befindet sich in Rellingen im Kreis Pinneberg. Von hier aus betreuen wir Hamburg und die gesamte Metropolregion im Umkreis von 50 km."
    },
    {
      q: "Wie schnell bekomme ich einen Termin?",
      a: "Als lokales Büro in Rellingen können wir sehr kurzfristig reagieren – oft noch am selben Tag. Durchschnittlich vergeben wir Termine innerhalb von 24-48 Stunden."
    },
    {
      q: "Kommt ihr auch zu mir nach Hause?",
      a: "Ja, absolut. Wir kommen dorthin, wo Ihr Fahrzeug steht – ob zu Hause in Rellingen, zur Arbeit oder zur Werkstatt. Mobiler Service ist unser Standard."
    },
    {
      q: "Was kostet das Gutachten?",
      a: "Bei einem unverschuldeten Unfall: nichts. Die gegnerische Haftpflichtversicherung übernimmt die Kosten für das Gutachten. Sie dürfen Ihren Gutachter frei wählen."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Unfallgutachten Rellingen | Kfz-Sachverständige vor Ort | Dein Gutachten Direkt</title>
        <meta name="description" content="Kfz-Sachverständigenbüro in Rellingen. Über 10 Jahre Erfahrung. Unfallgutachten direkt vor Ort. Kostenlos bei unverschuldetem Unfall. ☎ Jetzt anrufen!" />
        <meta name="keywords" content="Unfallgutachten Rellingen, Kfz Gutachter Rellingen, Sachverständiger Rellingen, Gutachter Pinneberg, Kfz Gutachten Rellingen" />
        <link rel="canonical" href="https://www.dein-gutachten-direkt.de/unfallgutachten-rellingen" />
        
        {/* LocalBusiness Schema - HAUPTSITZ */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Dein Gutachten Direkt - Hauptsitz Rellingen",
            "description": "Kfz-Sachverständigenbüro mit über 10 Jahren Erfahrung. Unabhängige Unfallgutachten in Rellingen, Hamburg und bundesweit.",
            "image": "https://customer-assets.emergentagent.com/job_gutachten-preview/artifacts/gusc9edr_image.png",
            "telephone": "+4941018471160",
            "email": "info@dein-gutachten-direkt.de",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Möwenstraße 2",
              "addressLocality": "Rellingen",
              "postalCode": "25462",
              "addressRegion": "Schleswig-Holstein",
              "addressCountry": "DE"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": "53.6333",
              "longitude": "9.8167"
            },
            "areaServed": [
              {"@type": "City", "name": "Rellingen"},
              {"@type": "City", "name": "Hamburg"},
              {"@type": "City", "name": "Pinneberg"},
              {"@type": "City", "name": "Norderstedt"},
              {"@type": "City", "name": "Halstenbek"},
              {"@type": "City", "name": "Schenefeld"}
            ],
            "openingHoursSpecification": {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
              "opens": "00:00",
              "closes": "23:59"
            },
            "priceRange": "€€",
            "paymentAccepted": "Rechnung, Überweisung",
            "currenciesAccepted": "EUR"
          })}
        </script>

        {/* FAQ Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.q,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.a
              }
            }))
          })}
        </script>
      </Helmet>

      <Header />

      <main>
        {/* Hero Section - Rellingen Fokus */}
        <section className="relative bg-gradient-to-br from-gray-900 via-black to-gray-800 pt-28 pb-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                {/* Hauptsitz Badge */}
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-green/20 border border-brand-green/30 rounded-full mb-6">
                  <MapPin className="w-4 h-4 text-brand-green" />
                  <span className="text-brand-green font-semibold text-sm">Unser Hauptsitz</span>
                </div>

                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                  Unfallgutachten
                  <br />
                  <span className="text-brand-green">in Rellingen</span>
                </h1>
                
                <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                  Kfz-Sachverständigenbüro mit <strong className="text-white">über 10 Jahren Erfahrung</strong>. 
                  Von unserem Hauptsitz in Rellingen betreuen wir Hamburg und die gesamte Metropolregion.
                </p>

                {/* USPs */}
                <div className="grid grid-cols-2 gap-4 mb-8">
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                    <span>Über 10 Jahre Erfahrung</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                    <span>Mehrere Sachverständige</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                    <span>Gerichtsfeste Gutachten</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                    <span>Zusammenarbeit mit Anwälten</span>
                  </div>
                </div>

                {/* CTAs */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <a href={`tel:${COMPANY_INFO.phone}`}>
                    <Button size="lg" className="w-full sm:w-auto bg-brand-green hover:bg-brand-green-dark text-white text-lg px-8 py-6 rounded-xl font-bold">
                      <Phone className="w-5 h-5 mr-2" />
                      Jetzt anrufen
                    </Button>
                  </a>
                  <a href={`https://wa.me/${COMPANY_INFO.whatsapp}`} target="_blank" rel="noopener noreferrer">
                    <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-white text-white hover:bg-white/10 text-lg px-8 py-6 rounded-xl font-bold">
                      <MessageCircle className="w-5 h-5 mr-2" />
                      WhatsApp
                    </Button>
                  </a>
                </div>

                <p className="text-gray-400 text-sm mt-6">
                  Bei unverschuldetem Unfall: Kostenübernahme durch gegnerische Versicherung
                </p>
              </div>

              {/* Info Box */}
              <div className="bg-white/5 backdrop-blur border border-white/10 rounded-3xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Unser Standort</h2>
                
                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-4">
                    <MapPin className="w-6 h-6 text-brand-green flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-white font-semibold">Adresse</p>
                      <p className="text-gray-400">Möwenstraße 2, 25462 Rellingen</p>
                      <p className="text-gray-400">Kreis Pinneberg, Schleswig-Holstein</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Clock className="w-6 h-6 text-brand-green flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-white font-semibold">Erreichbarkeit</p>
                      <p className="text-gray-400">24/7 telefonisch erreichbar</p>
                      <p className="text-gray-400">Termine oft noch am selben Tag</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Users className="w-6 h-6 text-brand-green flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-white font-semibold">Team</p>
                      <p className="text-gray-400">Mehrere erfahrene Sachverständige</p>
                      <p className="text-gray-400">Über 10 Jahre im Geschäft</p>
                    </div>
                  </div>
                </div>

                <div className="bg-brand-green/20 border border-brand-green/30 rounded-xl p-4">
                  <p className="text-brand-green font-semibold text-sm">
                    <strong>Freie Gutachterwahl:</strong> Bei unverschuldetem Unfall dürfen Sie Ihren Gutachter frei wählen. Die Kosten trägt die gegnerische Versicherung.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Über uns - Wer wir sind */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-12">
              <span className="text-brand-green font-semibold text-sm uppercase tracking-wider">Über uns</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2">Wer steckt hinter Dein Gutachten Direkt?</h2>
            </div>

            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed text-lg">
                <strong>Dein Gutachten Direkt</strong> ist ein Kfz-Sachverständigenbüro mit Hauptsitz in <strong>Rellingen (Kreis Pinneberg)</strong>. 
                Seit über <strong>10 Jahren</strong> erstellen wir unabhängige Unfallgutachten für Geschädigte in Hamburg, Schleswig-Holstein und bundesweit.
              </p>

              <p className="text-gray-700 leading-relaxed text-lg">
                Unser Team besteht aus <strong>mehreren erfahrenen Sachverständigen</strong>, die alle Fahrzeugtypen begutachten können – 
                vom Kleinwagen bis zum Transporter, vom Motorrad bis zum Elektrofahrzeug.
              </p>

              <div className="bg-gray-50 rounded-2xl p-8 my-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Das zeichnet uns aus:</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0 mt-1" />
                    <span><strong>Unabhängig:</strong> Wir arbeiten ausschließlich für Sie – nicht für Versicherungen oder Werkstätten</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0 mt-1" />
                    <span><strong>Erfahren:</strong> Über 10 Jahre Erfahrung in der Kfz-Schadensbewertung</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0 mt-1" />
                    <span><strong>Gerichtsfest:</strong> Unsere Gutachten sind rechtssicher und vor Gericht verwendbar</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0 mt-1" />
                    <span><strong>Anwaltsnetzwerk:</strong> Enge Zusammenarbeit mit Fachanwälten für Verkehrsrecht</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0 mt-1" />
                    <span><strong>Schnell:</strong> Termine meist innerhalb 24-48 Stunden, oft noch am selben Tag</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Was beinhaltet unser Gutachten */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-12">
              <span className="text-brand-green font-semibold text-sm uppercase tracking-wider">Leistungen</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2">Was beinhaltet unser Gutachten?</h2>
              <p className="text-xl text-gray-600 mt-4">Ein professionelles Unfallgutachten sichert Ihnen alle Ansprüche</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-brand-green/50 transition-colors">
                <h3 className="font-bold text-gray-900 text-lg mb-2">Reparaturkostenkalkulation</h3>
                <p className="text-gray-700">Detaillierte Berechnung aller Reparaturkosten nach Herstellervorgaben. Mit Arbeitslohn, Ersatzteilen und Lackierung.</p>
              </div>
              
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-brand-green/50 transition-colors">
                <h3 className="font-bold text-gray-900 text-lg mb-2">Wertminderung</h3>
                <p className="text-gray-700">Der merkantile Wertverlust Ihres Fahrzeugs nach dem Unfall – auch nach fachgerechter Reparatur. Oft mehrere hundert Euro zusätzlich.</p>
              </div>
              
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-brand-green/50 transition-colors">
                <h3 className="font-bold text-gray-900 text-lg mb-2">Nutzungsausfall</h3>
                <p className="text-gray-700">Entschädigung für die Zeit, in der Ihr Fahrzeug in der Werkstatt ist. Berechnet nach Fahrzeugklasse und Reparaturdauer.</p>
              </div>
              
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-brand-green/50 transition-colors">
                <h3 className="font-bold text-gray-900 text-lg mb-2">Wiederbeschaffungswert</h3>
                <p className="text-gray-700">Bei Totalschaden: Was kostet ein gleichwertiges Ersatzfahrzeug? Wir ermitteln den fairen Marktwert.</p>
              </div>
              
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-brand-green/50 transition-colors">
                <h3 className="font-bold text-gray-900 text-lg mb-2">Restwertermittlung</h3>
                <p className="text-gray-700">Der Verkaufswert Ihres beschädigten Fahrzeugs. Relevant für die Abrechnung bei Totalschaden.</p>
              </div>
              
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-brand-green/50 transition-colors">
                <h3 className="font-bold text-gray-900 text-lg mb-2">Fotodokumentation</h3>
                <p className="text-gray-700">Umfangreiche Beweissicherung mit professionellen Fotos aller Schäden aus verschiedenen Winkeln.</p>
              </div>
            </div>

            <div className="bg-brand-green/10 border-2 border-brand-green/30 rounded-2xl p-6 mt-8 text-center">
              <p className="text-gray-900 font-semibold">
                <strong>Wichtig:</strong> Ein Werkstatt-Kostenvoranschlag enthält nur die Reparaturkosten. 
                Wertminderung und Nutzungsausfall fehlen dort – das kann Sie mehrere hundert Euro kosten!
              </p>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Häufige Fragen</h2>
            <div className="space-y-4">
              {faqs.map((faq, idx) => (
                <div key={idx} className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                  <h3 className="font-bold text-gray-900 mb-3 text-lg">{faq.q}</h3>
                  <p className="text-gray-700 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Einsatzgebiet */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Unser Einsatzgebiet ab Rellingen</h2>
            <p className="text-lg text-gray-700 mb-8">
              Von Rellingen aus sind wir in 30 Minuten in Hamburg und betreuen die gesamte Metropolregion im Umkreis von 50 km.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {['Rellingen', 'Pinneberg', 'Hamburg', 'Norderstedt', 'Halstenbek', 'Schenefeld', 'Elmshorn', 'Wedel', 'Uetersen', 'Quickborn'].map((ort) => (
                <span key={ort} className="px-4 py-2 bg-white rounded-lg border border-gray-200 text-gray-700 font-medium">
                  {ort}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-gradient-to-br from-gray-900 to-black">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold text-white mb-6">Jetzt Gutachten beauftragen</h2>
            <p className="text-xl text-gray-300 mb-8">Hauptsitz Rellingen • Termine oft noch am selben Tag</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={`tel:${COMPANY_INFO.phone}`}>
                <Button size="lg" className="bg-brand-green hover:bg-brand-green-dark text-white text-lg px-10 py-6 rounded-xl font-bold">
                  <Phone className="w-5 h-5 mr-2" />
                  04101 847 11 60
                </Button>
              </a>
              <a href={`https://wa.me/${COMPANY_INFO.whatsapp}`} target="_blank" rel="noopener noreferrer">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 text-lg px-10 py-6 rounded-xl font-bold">
                  <MessageCircle className="w-5 h-5 mr-2" />
                  WhatsApp
                </Button>
              </a>
            </div>
          </div>
        </section>

        <InternalLinksSection currentPage="/unfallgutachten-rellingen" />
      </main>

      <Footer />
    </div>
  );
};

export default UnfallgutachtenRellingenPage;
